import * as React from "react"
import { usePage } from "@inertiajs/react"
import { Check } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

type Remote = { photos: LibraryPhoto[]; more: boolean; page: number }

// The library the caller hands us is capped, so filtering it in the browser
// would quietly hide anything past the cap. Section and search go to the
// server instead; the passed-in list is the first paint and the fallback if
// that request ever fails.
export function PhotoLibraryDialog({
  open,
  onOpenChange,
  library,
  onSelect,
  multiple = false,
  onSelectMany,
  placedIds = [],
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  library: LibraryPhoto[]
  onSelect: (photo: LibraryPhoto) => void
  /** Tick several photos and add them in one go — a room's gallery is a
   *  dozen photos at a time, not one. */
  multiple?: boolean
  onSelectMany?: (photos: LibraryPhoto[]) => void
  /** Already in the slot: shown as added rather than offered again. */
  placedIds?: number[]
}) {
  const { props } = usePage<PageProps>()
  const sections = props.photo_sections ?? []

  const [search, setSearch] = React.useState("")
  const [section, setSection] = React.useState("")
  const [remote, setRemote] = React.useState<Remote | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [picked, setPicked] = React.useState<LibraryPhoto[]>([])

  function query(page: number) {
    const params = new URLSearchParams()
    if (section) params.set("section", section)
    if (search.trim()) params.set("q", search.trim())
    if (page > 1) params.set("page", String(page))
    return `/admin/photos/library?${params.toString()}`
  }

  // Refetch page one whenever the filters settle. The debounce keeps a typed
  // word from firing a request per keystroke.
  React.useEffect(() => {
    if (!open) return

    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      try {
        const response = await fetch(query(1), {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (!response.ok) throw new Error(String(response.status))
        const data = await response.json()
        if (!cancelled) setRemote({ photos: data.photos, more: data.more, page: 1 })
      } catch {
        // Fall back to the list we were handed rather than showing nothing.
        if (!cancelled) setRemote(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 200)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, section])

  async function loadMore() {
    if (!remote) return
    setLoading(true)
    try {
      const response = await fetch(query(remote.page + 1), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error(String(response.status))
      const data = await response.json()
      setRemote({ photos: [...remote.photos, ...data.photos], more: data.more, page: remote.page + 1 })
    } catch {
      // Keep what's already on screen.
    } finally {
      setLoading(false)
    }
  }

  const photos = remote?.photos ?? library
  const placed = React.useMemo(() => new Set(placedIds), [placedIds])
  // A filtered view is the natural batch — "everything in the Oasis Room
  // section" — so Select all follows the filters rather than the library.
  const selectable = photos.filter((photo) => !placed.has(photo.id))
  const allPicked = selectable.length > 0 && picked.length === selectable.length

  function reset() {
    setSearch("")
    setSection("")
    setRemote(null)
    setPicked([])
  }

  function handleSelect(photo: LibraryPhoto) {
    onSelect(photo)
    onOpenChange(false)
    reset()
  }

  function toggle(photo: LibraryPhoto) {
    setPicked((prev) =>
      prev.some((p) => p.id === photo.id) ? prev.filter((p) => p.id !== photo.id) : [...prev, photo],
    )
  }

  function addPicked() {
    if (picked.length === 0) return
    onSelectMany?.(picked)
    onOpenChange(false)
    reset()
  }

  // Closing the dialog throws the ticks away — reopening starts clean, the
  // way an unsubmitted form does.
  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{multiple ? "Choose photos" : "Choose a photo"}</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search by alt text, label, or tag…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-48 flex-1"
          />
          {sections.length > 0 && (
            <Select
              id="photo-library-section"
              aria-label="Filter by section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="w-48"
            >
              <option value="">All sections</option>
              <option value="none">Unfiled</option>
              {sections.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
          {multiple && selectable.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPicked(allPicked ? [] : selectable)}
            >
              {allPicked ? "Clear" : `Select all ${selectable.length}`}
            </Button>
          )}
        </div>

        <div
          id="photo-library-grid"
          className="grid max-h-[60vh] gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4"
        >
          {photos.length === 0 && (
            <p className="col-span-full py-8 text-center text-ink-muted">
              {loading ? "Searching…" : "No photos found."}
            </p>
          )}
          {photos.map((photo) => {
            const isPlaced = placed.has(photo.id)
            const isPicked = picked.some((p) => p.id === photo.id)

            return (
              <button
                key={photo.id}
                type="button"
                onClick={() => (multiple ? toggle(photo) : handleSelect(photo))}
                disabled={isPlaced}
                aria-pressed={multiple ? isPicked : undefined}
                aria-label={isPlaced ? `${photo.alt_text_en ?? "Photo"} — already added` : undefined}
                className={cn(
                  "relative cursor-pointer overflow-hidden rounded-lg border text-left transition-colors",
                  isPicked ? "border-accent ring-2 ring-accent" : "border-hairline hover:border-accent",
                  isPlaced && "cursor-default opacity-50 hover:border-hairline",
                )}
              >
                {photo.url ? (
                  <img src={photo.url} alt={photo.alt_text_en ?? ""} className="aspect-video w-full object-cover" />
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-surface text-xs text-ink-muted">
                    No preview
                  </div>
                )}
                {isPicked && (
                  <span className="absolute left-2 top-2 rounded-full bg-accent p-1 text-white shadow">
                    <Check className="h-3 w-3" />
                  </span>
                )}
                {isPlaced && (
                  <span className="absolute left-2 top-2 rounded bg-ink-display/80 px-1.5 py-0.5 text-[11px] text-white">
                    Added
                  </span>
                )}
                {photo.section_name && (
                  <p className="truncate px-2 py-1 text-[11px] text-ink-muted">{photo.section_name}</p>
                )}
              </button>
            )
          })}

          {/* Inside the list, after the last tile: it extends the list, and
              out here it would be half-covered by the sticky bar. */}
          {remote?.more && (
            <div className="col-span-full flex justify-center py-1">
              <Button type="button" variant="secondary" size="sm" onClick={loadMore} disabled={loading}>
                {loading ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>

        {multiple && (
          // A phone fits about two rows of photos, so the tile list is longer
          // than the screen and the dialog scrolls. The bar stays stuck to the
          // bottom of it: the count and the Add button are in reach after the
          // first tick rather than after a scroll past the last photo. The
          // negative margins let it span the dialog's own padding.
          <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-hairline bg-page px-6 pb-6 pt-3">
            <p className="mr-auto text-sm text-ink-muted">
              {picked.length === 0
                ? "Tick as many as you like."
                : `${picked.length} photo${picked.length === 1 ? "" : "s"} selected`}
            </p>
            <Button type="button" variant="secondary" size="sm" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={picked.length === 0} onClick={addPicked}>
              {picked.length > 1 ? `Add ${picked.length} photos` : "Add photo"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
